import {
  lazy,
  Suspense,
  useCallback,
  useEffect,
  useMemo,
  useState
} from 'react'
import type {
  ColDef,
  ColGroupDef,
  ColumnVisibleEvent,
  FilterChangedEvent,
  GetMainMenuItems,
  GridApi,
  ICombinedSimpleModel,
  IServerSideGetRowsParams,
  IServerSideGetRowsRequest,
  ISimpleFilterModel,
  ISimpleFilterParams
} from 'ag-grid-community'
import { intersection, isPlainObject } from 'lodash-es'
import { toast } from 'react-toastify'
import {
  createScientificNotationValueFormatter,
  headerWithSubheadingComponentGenerator
} from '@jana-technology/lht-qra-table/dist/utils'

import { QRADatasetRow } from '$/types'
import {
  useGetDataManagementDefaultDataViewColumnDefinitionsQuery,
  useLazyGetDataManagementDefaultDataViewTableRowsQuery,
  type IExtendedColDef
} from 'app/services/api/agGrid/agGrid'
import type { IColorByValueConfig } from 'app/services/api/colorByValue'
import LoadingSpinner from 'common/components/LoadingSpinner'
import type { QRATableWrapperType } from 'common/components/QRATableWrapper'
import { generateColDefsIgnoreFields } from 'common/utils/agGrid/helpers'

import { getValueFormatter } from 'features/RiskManagement/Components/Tables/utils/formatters'
import { autoSizeAllColumns } from 'features/RiskManagement/helpers'

import { initialColDefsModifier } from './utils/initialColDefsModifier'
import syncCBVConfigToColDefs from './utils/syncCBVConfigToColDefs'

const QRATableWrapperLazy = lazy<QRATableWrapperType<QRADatasetRow>>(
  () => import('../../../../common/components/QRATableWrapper/QRATableWrapper')
)

export type IDataTableFilterType = 'set' | 'number' | 'date'

export interface IDataTableFilterUnit extends ISimpleFilterModel {
  filterType: IDataTableFilterType

  // filterType: set
  values?: string[]
  codeValues?: string[]

  // filterType: number
  filter?: number
  // inRange type
  filterTo?: number

  // filterType: 'date'
  dateFrom?: string
  dateTo?: string | null
}

export interface IDataTableFilterModel {
  [code: string]:
    | IDataTableFilterUnit
    | ICombinedSimpleModel<IDataTableFilterUnit>
}

interface IDataViewTable {
  systemScopeId: string | undefined
  systemScopeUpdatedAt: number
  tableType: 'default' | 'custom'
  tableId: string | undefined
  // NOTE: Will also make use of attribute type properties for filter
  colorByValueConfig?: IColorByValueConfig
  onFilterChangeCB?: (filters: IDataTableFilterModel) => void
}

/**
 * NOTE: Full remount of component on system scope update since
 *       we want to run all the init logic anyway.
 */
const DataTable = ({
  systemScopeId,
  systemScopeUpdatedAt,
  tableType,
  tableId,
  colorByValueConfig,
  onFilterChangeCB
}: IDataViewTable) => {
  const {
    currentData: defaultDataViewColDefs,
    isSuccess: defaultDataViewColDefsLoaded
  } = useGetDataManagementDefaultDataViewColumnDefinitionsQuery(
    {
      params: {
        viewId: tableId!,
        systemScopeId: systemScopeId!
      },
      cacheKeys: {
        systemScopeUpdatedAt
      }
    },
    {
      skip: tableType !== 'default' || tableId == null || systemScopeId == null
    }
  )

  const [req] = useLazyGetDataManagementDefaultDataViewTableRowsQuery()
  // it can't be false because that disables the sidebar. need to investigate QRATable
  const [sideBarVisible, setSideBarVisible] = useState<string>()

  const getAttrCodesByNames = (
    defaultDataViewColDefs: IExtendedColDef[],
    attrValues: string[],
    colDefCode: string
  ): string[] =>
    attrValues.map((value: string) => {
      const valueColDef = defaultDataViewColDefs.find(
        (colDef) => colDef.code === colDefCode
      )

      if (valueColDef) {
        return valueColDef.attributeEnums?.find(
          (attrEnum) => attrEnum.name === value
        )?.code as string
      }

      return value
    })

  const onFilterChanged = (evt: FilterChangedEvent) => {
    const model = evt.api.getFilterModel() as IDataTableFilterModel
    const modelWithCodes = { ...model }

    Object.keys(model).forEach((colDefCode: string) => {
      const attribute = model[colDefCode] as IDataTableFilterUnit

      if (attribute.filterType === 'set' && attribute.values) {
        const isBoolish = ['true', 'false'].includes(attribute.values.join())

        if (!isBoolish) {
          const attrCodes = getAttrCodesByNames(
            defaultDataViewColDefs!,
            attribute.values,
            colDefCode
          )

          const attributeWithCodes = {
            ...attribute,
            codeValues: attrCodes
          }

          modelWithCodes[colDefCode] = attributeWithCodes
        }
      }
    })
    onFilterChangeCB?.(modelWithCodes)
  }

  const onColumnVisible = (evt: ColumnVisibleEvent) => {
    const visibilityChangedColumn = evt.column?.getColId() as string

    evt.api.refreshCells({ columns: [visibilityChangedColumn], force: true })
  }

  const [gridApi, setGridApi] = useState<GridApi<QRADatasetRow>>()

  useEffect(() => {
    // NOTE: This needs to be updated in effect to bind CBV selection updates
    if (gridApi) {
      syncCBVConfigToColDefs(gridApi, colorByValueConfig)
    }
  }, [colorByValueConfig, gridApi])

  /**
   * Extra logic to workaround issue where custom header is
   * not respecting sort configurations
   *
   * e.g. sorting is not allowed on data source column
   */
  const filterOutUnsupportedParams = (
    colDefs: (ColDef | ColGroupDef)[] | undefined,
    requestParams: IServerSideGetRowsRequest
  ) => {
    if (colDefs === undefined) {
      return requestParams
    }

    // copy requestParams here to avoid mutation bugs
    const filteredRequestParams = {
      ...requestParams,
      sortModel: requestParams.sortModel.filter(
        (sm) => sm.colId !== 'ag-Grid-AutoColumn'
      )
    }

    const { sortModel } = filteredRequestParams

    colDefs
      .filter((cd) => (cd as ColGroupDef).children == null)
      .filter((cd: ColDef) => !cd.sortable)
      .forEach((cd: ColDef) => {
        let idx = -1

        while ((idx = sortModel.findIndex((i) => i.colId === cd.field)) >= 0) {
          // NOTE: mutation
          sortModel.splice(idx, 1)
        }
      })

    return filteredRequestParams
  }

  /**
   * @param IServerSideGetRowParams's request prop
   * @returns string[]
   */
  const getDuplicateGroupAggregationFields = ({
    rowGroupCols,
    valueCols
  }: IServerSideGetRowsRequest) => {
    const groupIds = rowGroupCols.map((rg) => rg.displayName)
    const aggIds = valueCols.map((vc) => vc.displayName)

    return intersection(groupIds, aggIds)
  }

  // NOTE: Only the initial instance given to table is used
  const serverSideDatasourceGetRows = useCallback(
    (agParams: IServerSideGetRowsParams) => {
      if (tableId == null || systemScopeId == null) {
        console.error('SSRM: Table/SystemScope ID missing')
        agParams.fail()
      } else {
        const duplicateFields = getDuplicateGroupAggregationFields(
          agParams.request
        )

        if (duplicateFields.length > 0) {
          toast.warn(
            [
              duplicateFields.join(', '),
              'columns(s) cannot be grouped and aggregated at',
              'the same time, please adjust your selections.'
            ].join(' ')
          )
          agParams.fail()

          return
        }

        // get the selected key or check parentNode (looking for a group field with null value)
        const prepKeys = agParams.request.groupKeys.length
          ? agParams.request.groupKeys
          : agParams.parentNode.data

        // if no key was selected, derive one from parentNode; default to an empty array (data can be null)
        // set key as 'null' to match groupKeys type; will be pre-processed by rtk query back to null
        const groupKeys = isPlainObject(prepKeys)
          ? [String(agParams.parentNode.key)]
          : (prepKeys ?? [])

        const normalizedRequest = {
          ...agParams.request,
          groupKeys
        }

        req(
          {
            params: {
              viewId: tableId
            },
            body: {
              ...filterOutUnsupportedParams(
                agParams.api.getColumnDefs(),
                normalizedRequest
              ),
              systemScopeId,
              sourceType: 'views'
            },
            cacheKeys: {
              systemScopeUpdatedAt
            }
          },
          true // preferCacheValue won't work as expected anyway: RTK#2802
        )
          .unwrap()
          .then((res) => {
            agParams.success({
              rowData: res.rows,
              rowCount: res.lastRow
            })
          })
          .catch((err) => {
            console.error(err)
            agParams.fail()
          })
      }
    },
    [tableId, req, systemScopeId, systemScopeUpdatedAt]
  )

  const headerWithSubheadingComponent = useMemo(
    () =>
      headerWithSubheadingComponentGenerator(
        'unitTypeName',
        'h-4 font-normal tracking-[0.03em]'
      ),
    []
  )

  const colDefs = useMemo(() => {
    const ignoreFields = generateColDefsIgnoreFields(
      defaultDataViewColDefs,
      'year'
    )?.concat(generateColDefsIgnoreFields(defaultDataViewColDefs, 'date') || [])

    // expecting more fields to override applyscientificNotation formatter
    const additionalIgnoreFields = [
      'Length',
      'FromMeasure',
      'ToMeasure',
      'Measure',
      'BlastingSusceptibility',
      'CarbonDioxideMoleFraction',
      'CharpyVNotchToughness',
      'CpSystemPerformance',
      'DependentCustomerCount',
      'DepthOfCover',
      'DepthOfFrostLine',
      'EarthMvmntSusceptibility',
      'ExternalCorrosionRate',
      'FloodSusceptibility',
      'WeldCoatingExtent',
      'HydrogenSulfideMoleFraction',
      'InternalCorrosionRate',
      'IsolationSectionVolume',
      'LightningStrikeDensity',
      'OneCallResponseTime',
      'OperatingPressure',
      'OperatingTemperature',
      'OutsideDiameter',
      'OxygenMoleFraction',
      'PatrolInterval',
      'ReassessmentInterval',
      'SoilResistivity',
      'Ssi',
      'SubsidenceSusceptibility',
      'TensileStrength',
      'WaterMoleFraction',
      'YieldStrength',
      'YieldStrengthPsi'
    ]

    const updatedIgnoreFields = ignoreFields?.concat(additionalIgnoreFields)

    const applyscientificNotation = createScientificNotationValueFormatter(
      2,
      updatedIgnoreFields
    )

    return applyscientificNotation(
      initialColDefsModifier(defaultDataViewColDefs ?? []).map((colDef) => ({
        ...colDef,
        valueFormatter: getValueFormatter(additionalIgnoreFields, colDef.field)
      }))
    )
  }, [defaultDataViewColDefs])

  const getContextMenuItems: GetMainMenuItems = (params) => {
    const defaultItems = params.defaultItems

    // REVIEW: it was typed string[] but we can push object into it?
    const newItems = defaultItems.filter((item: string) => {
      return item !== 'rowGroup'
    }) as Array<
      | string
      | {
          name: string
          action: () => void
        }
    >

    newItems.push({
      name: 'Expand All',
      action: () => {
        gridApi?.expandAll()
      }
    })

    newItems.push({
      name: 'Collapse All',
      action: () => {
        gridApi?.collapseAll()
      }
    })

    return newItems
  }

  const memoizedDefaultColDef = useMemo(() => {
    return {
      enableValue: true,
      enableRowGroup: true,
      sortable: true,
      filterParams: {
        buttons: ['reset', 'cancel', 'apply'],
        closeOnApply: true,
        maxValidYear: 9999
      } as Partial<ISimpleFilterParams>,
      allowedAggFuncs: ['sum', 'avg', 'count', 'max', 'min'],
      headerComponent: headerWithSubheadingComponent
    }
  }, [headerWithSubheadingComponent])

  return (
    <LoadingSpinner isLoading={tableId == null}>
      <div className='ag-theme-lighthouse-ssrm h-full'>
        {defaultDataViewColDefsLoaded && (
          <Suspense fallback='Loading table...'>
            <QRATableWrapperLazy
              ssrm
              // NOTE: AGGrid seems to make a copy of the whole object
              //       which is reason to conditionally rendering the table
              serverSideDatasource={{
                getRows: serverSideDatasourceGetRows
              }}
              defaultColDef={memoizedDefaultColDef}
              getMainMenuItems={getContextMenuItems}
              colDefs={colDefs}
              rowData={[]}
              getRowId={() => {
                return crypto.randomUUID()
              }}
              hidePivotToggle
              sideBar={{
                toolPanels: ['columns', 'filters'],
                position: 'right',
                hiddenByDefault: false,
                defaultToolPanel: sideBarVisible
              }}
              showSidebar
              suppressAutoResize
              suppressCsvExport
              suppressExcelExport
              onFilterChanged={onFilterChanged}
              onColumnVisible={onColumnVisible}
              onGridReady={(event) => setGridApi(event.api)}
              onStateUpdated={(event) =>
                event.api.refreshCells({ force: true })
              }
              onToolPanelVisibleChanged={(event) => {
                setSideBarVisible(event.visible ? event.key : undefined)
              }}
              onFirstDataRendered={autoSizeAllColumns}
              suppressColumnVirtualisation={true}
            />
          </Suspense>
        )}
      </div>
    </LoadingSpinner>
  )
}

export default DataTable
